import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";

const HOVER_SELECTOR = 'a, button, [role="button"], .mt-btn, .mt-pill, .mt-nav-link, input, textarea, select, [data-cursor-hover]';

export default function GlassCursor() {
  const { reduceMotion } = useTheme();
  const cursorRef = useRef(null);
  const trailLayerRef = useRef(null);
  const lastTrailRef = useRef(0);

  useEffect(() => {
    if (reduceMotion) return undefined;
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;

    const cursor = cursorRef.current;
    const trailLayer = trailLayerRef.current;
    if (!cursor || !trailLayer) return undefined;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;

    const onMove = (e) => {
      mx = e.clientX; my = e.clientY;
      // 1:1 realtime — set position immediately on mousemove
      cursor.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;

      // throw a tiny trail particle (throttled)
      const now = performance.now();
      if (now - lastTrailRef.current > 28) {
        lastTrailRef.current = now;
        const t = document.createElement("span");
        t.className = "mt-cursor-trail";
        t.style.left = `${mx}px`;
        t.style.top = `${my}px`;
        trailLayer.appendChild(t);
        window.setTimeout(() => { t.remove(); }, 500);
      }
    };

    const onDown = () => {
      cursor.classList.add("is-click");
      const r = document.createElement("span");
      r.className = "mt-cursor-ripple";
      r.style.left = `${mx}px`;
      r.style.top = `${my}px`;
      trailLayer.appendChild(r);
      window.setTimeout(() => r.remove(), 500);
    };
    const onUp = () => cursor.classList.remove("is-click");

    const onOver = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(HOVER_SELECTOR)) cursor.classList.add("is-hover");
    };
    const onOut = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(HOVER_SELECTOR)) cursor.classList.remove("is-hover");
    };

    // initial position
    cursor.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
    };
  }, [reduceMotion]);

  const [canHover, setCanHover] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setCanHover(mq.matches);
    const onChange = (e) => setCanHover(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  if (reduceMotion || !canHover) return null;

  return (
    <>
      <div ref={trailLayerRef} aria-hidden="true" data-testid="glass-cursor-trail" />
      <div ref={cursorRef} className="mt-cursor mt-keep-anim" aria-hidden="true" data-testid="glass-cursor">
        <div className="mt-cursor-halo" />
        <svg className="mt-cursor-arrow" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 2L2 20L7.5 15.5L11 22L13.5 21L10 14.5L17 14.5L2 2Z"
            fill="white" stroke="rgba(0,170,255,0.6)" strokeWidth="1" strokeLinejoin="round"/>
        </svg>
      </div>
    </>
  );
}
